import { useState } from "react";
import { MessageCircle, Instagram, Link2, MapPin, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import SectionHeading from "./SectionHeading";
import {
  WHATSAPP_URL,
  WHATSAPP_DISPLAY,
  INSTAGRAM_URL,
  LINKTREE_URL,
  UNIDADES,
  MODALIDADES_INTERESSE,
} from "../data/siteData";

const initial = { responsavel: "", atleta: "", ano: "", telefone: "", modalidade: MODALIDADES_INTERESSE[0], mensagem: "" };

const Contato = () => {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const enviar = (e) => {
    e.preventDefault();
    const msg = [
      "Olá, Soccer Trainer! Vim pelo site.",
      `Responsável: ${form.responsavel}`,
      `Atleta: ${form.atleta}`,
      `Ano de nascimento: ${form.ano}`,
      `Telefone/WhatsApp: ${form.telefone}`,
      `Modalidade de interesse: ${form.modalidade}`,
      form.mensagem ? `Mensagem: ${form.mensagem}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-lawn focus:outline-none";

  return (
    <section id="contato" data-testid="contato-section" className="relative bg-navy py-20 md:py-28">
      <div className="turf-lines pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Fale com a gente"
          title="Contato"
          lead="Preencha o formulário e continue a conversa direto no nosso WhatsApp."
        />

        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contato-whatsapp-link"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-night/70 p-5 transition-colors hover:border-lawn/50"
              >
                <MessageCircle size={26} className="shrink-0 text-lawn" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-white/50">WhatsApp — Thales Affá</p>
                  <p className="font-heading text-lg font-semibold text-white">{WHATSAPP_DISPLAY}</p>
                </div>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contato-instagram-link"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-night/70 p-5 transition-colors hover:border-gold/50"
              >
                <Instagram size={26} className="shrink-0 text-gold" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-white/50">Instagram</p>
                  <p className="font-heading text-lg font-semibold text-white">@soccertrainerpatos</p>
                </div>
              </a>
              <a
                href={LINKTREE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contato-linktree-link"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-night/70 p-5 transition-colors hover:border-lawn/50"
              >
                <Link2 size={26} className="shrink-0 text-lawn" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-white/50">Linktree</p>
                  <p className="font-heading text-lg font-semibold text-white">linktr.ee/soccertrainer</p>
                </div>
              </a>
              <div className="rounded-2xl border border-white/10 bg-night/70 p-5">
                <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50">
                  <MapPin size={15} className="text-gold" /> Endereços
                </p>
                {UNIDADES.map((u) => (
                  <p key={u.nome} className="mb-2 text-sm leading-relaxed text-white/80 last:mb-0">
                    <strong className="text-white">{u.nome}:</strong> {u.endereco}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={enviar}
              data-testid="contact-form"
              className="rounded-3xl border border-white/10 bg-night/70 p-6 backdrop-blur md:p-9"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="f-responsavel" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Nome do responsável *</label>
                  <input id="f-responsavel" data-testid="form-responsavel" required value={form.responsavel} onChange={set("responsavel")} placeholder="Seu nome" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-atleta" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Nome do atleta *</label>
                  <input id="f-atleta" data-testid="form-atleta" required value={form.atleta} onChange={set("atleta")} placeholder="Nome da criança/jovem" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-ano" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Ano de nascimento *</label>
                  <input id="f-ano" data-testid="form-ano" type="number" min="2008" max="2022" required value={form.ano} onChange={set("ano")} placeholder="Ex.: 2015" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-telefone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Telefone/WhatsApp *</label>
                  <input id="f-telefone" data-testid="form-telefone" type="tel" required value={form.telefone} onChange={set("telefone")} placeholder="(34) 99999-9999" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="f-modalidade" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Modalidade de interesse</label>
                  <select id="f-modalidade" data-testid="form-modalidade" value={form.modalidade} onChange={set("modalidade")} className={inputCls}>
                    {MODALIDADES_INTERESSE.map((m) => (
                      <option key={m} value={m} className="bg-night">{m}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="f-mensagem" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/60">Mensagem</label>
                  <textarea id="f-mensagem" data-testid="form-mensagem" rows={4} value={form.mensagem} onChange={set("mensagem")} placeholder="Conte um pouco sobre o atleta ou tire suas dúvidas..." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-whatsapp"
                className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lawn px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy shadow-[0_0_35px_rgba(141,198,63,0.3)] transition-transform duration-200 hover:scale-[1.02] sm:w-auto"
              >
                <Send size={17} strokeWidth={2.5} />
                Enviar pelo WhatsApp
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contato;
